export type AudioPlaybackPipeline = {
  appendChunk: (bytes: Uint8Array, sentenceId: number) => void;
  finishStreaming: () => void;
  playbackDone: Promise<void>;
  cleanup: () => void;
};

// Plays AI audio using the Web Audio API, grouped by sentence_id.
//
// The server streams each AI turn as multiple MP3 chunks split across sentences
// (sentence_id=1 seq=1, seq=2, seq=3 ... sentence_id=2 seq=4 ...). Individual chunks
// are raw MP3 stream fragments — not independently decodable. Grouping by sentence_id
// and concatenating gives a complete MP3 that AudioContext.decodeAudioData can handle.
const createAudioPlaybackPipeline = (
  onAiMediaStream: (stream: MediaStream | null) => void,
  signal: AbortSignal,
): AudioPlaybackPipeline => {
  const ctx = new AudioContext();
  const streamDestination = ctx.createMediaStreamDestination();
  let nextStartTime = 0;
  let hasNotifiedStream = false;
  let resolvePlayback: (() => void) | null = null;
  let pendingSentences = 0;
  let isDoneStreaming = false;

  const sentenceBuffers = new Map<number, Uint8Array[]>();
  let lastSeenSentenceId: number | null = null;
  let schedulingChain: Promise<void> = Promise.resolve();

  const playbackDone = new Promise<void>((resolve) => {
    resolvePlayback = resolve;
    signal.addEventListener('abort', () => resolve(), { once: true });
  });

  const checkDone = () => {
    if (isDoneStreaming && pendingSentences === 0 && resolvePlayback) {
      onAiMediaStream(null);
      resolvePlayback();
      resolvePlayback = null;
    }
  };

  const decodeSentence = (chunks: Uint8Array[]) => {
    pendingSentences += 1;

    const totalLength = chunks.reduce((sum, c) => sum + c.byteLength, 0);
    const merged = new Uint8Array(totalLength);
    let offset = 0;
    chunks.forEach((c) => { merged.set(c, offset); offset += c.byteLength; });

    // Kick off decode immediately so sentences decode in parallel while the
    // previous one is still playing. schedulingChain then awaits the already-
    // started promise, preserving in-order scheduling without serialising decodes.
    const decodePromise = ctx.decodeAudioData(merged.buffer);

    schedulingChain = schedulingChain.then(async () => {
      if (signal.aborted) { pendingSentences -= 1; checkDone(); return; }
      try {
        const decoded = await decodePromise;
        if (signal.aborted) { pendingSentences -= 1; checkDone(); return; }

        // Ensure AudioContext is running — may be suspended by browser autoplay policy
        await ctx.resume().catch(() => {});

        const source = ctx.createBufferSource();
        source.buffer = decoded;
        source.connect(ctx.destination);
        source.connect(streamDestination);

        const startAt = Math.max(ctx.currentTime, nextStartTime);
        source.start(startAt);
        nextStartTime = startAt + decoded.duration;

        if (!hasNotifiedStream) {
          hasNotifiedStream = true;
          onAiMediaStream(streamDestination.stream);
        }

        source.onended = () => { pendingSentences -= 1; checkDone(); };
      } catch {
        pendingSentences -= 1;
        checkDone();
      }
    });
  };

  const appendChunk = (bytes: Uint8Array, sentenceId: number) => {
    // When sentenceId changes the previous sentence is complete — decode it now
    if (lastSeenSentenceId !== null && lastSeenSentenceId !== sentenceId) {
      const completed = sentenceBuffers.get(lastSeenSentenceId);
      if (completed?.length) decodeSentence(completed);
      sentenceBuffers.delete(lastSeenSentenceId);
    }
    lastSeenSentenceId = sentenceId;
    const bucket = sentenceBuffers.get(sentenceId) ?? [];
    bucket.push(bytes);
    sentenceBuffers.set(sentenceId, bucket);
  };

  const finishStreaming = () => {
    // Decode whatever remains in the last sentence
    if (lastSeenSentenceId !== null) {
      const remaining = sentenceBuffers.get(lastSeenSentenceId);
      if (remaining?.length) decodeSentence(remaining);
      sentenceBuffers.delete(lastSeenSentenceId);
    }
    isDoneStreaming = true;
    checkDone();
  };

  const cleanup = () => {
    ctx.close().catch(() => {});
    sentenceBuffers.clear();
    onAiMediaStream(null);
    if (resolvePlayback) { resolvePlayback(); resolvePlayback = null; }
  };

  return { appendChunk, finishStreaming, playbackDone, cleanup };
};

export default createAudioPlaybackPipeline;
